import { useState, type FormEvent, type ReactNode } from 'react';
import {
  diagnostics,
  matter,
  system,
  type NepodporovaneMatter,
  type PripravenostMatter,
} from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { Badge, Button, Card, DataRow, Field, Notice, PageHeader } from '../components/ui.js';
import { IconQr, IconRefresh } from '../components/icons.js';
import { kameraDostupna, SkenerQr } from '../components/qr-scan.js';
import { MapaSieteThread } from './thread-siet.js';
import { useSession } from '../auth/session.js';

/*
 * Stranka Matter.
 *
 * Vlastna stranka, nie karta v Integraciach: parovanie je postup s
 * vlastnym priebehom a sonda pripravenosti siete k nemu patri hned
 * vedla - rovnako ako maju svoju stranku Tuya a Zigbee (ziadost
 * zadavatela 2026-09-16).
 */

export function MatterPage(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const nacitanie = useApi((signal) => matter.prehlad(signal));
  const [kod, setKod] = useState('');
  const [skenuje, setSkenuje] = useState(false);
  const [paruje, setParuje] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [hotovo, setHotovo] = useState<string | undefined>(undefined);

  const sparuj = (event: FormEvent): void => {
    event.preventDefault();
    setChyba(undefined);
    setHotovo(undefined);
    setParuje(true);
    void matter
      .commission(kod)
      .then(({ device }) => {
        setHotovo(device.name);
        setKod('');
        nacitanie.refresh();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenie sa nepodarilo spárovať.');
      })
      .finally(() => setParuje(false));
  };

  const data = nacitanie.data;
  if (data === undefined) return null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Matter"
        detail="Párovanie zariadení Matter a pripravenosť siete."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />
      <Card className="flex flex-col gap-4 p-4 sm:p-5">
        {!data.enabled ? (
          <>
            <p className="text-sm text-(--color-text-muted)">
              Matter je vypnutý. Potrebuje sieť hostiteľa, funkčné IPv6 a mDNS bez reflektorov —
              preto sa zapína vedome premennou <code>LOXLINK_MATTER_ENABLED</code>, až keď sonda
              nižšie povie, že sieť na to je.
            </p>
            <SondaMatter />
          </>
        ) : (
          <>
            <p className="text-sm text-(--color-text-muted)">
              Spárované zariadenia zostávajú v modeli aj po reštarte — fabrika je uložená. Párovací
              kód je na krabici alebo na displeji zariadenia.
            </p>

            {data.devices.length > 0 && (
              <ul className="flex flex-col gap-2">
                {data.devices.map((d) => (
                  <li
                    key={d.nodeId}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    {/*
                      Náš názov nahor, `peerN` pod ním.

                      Číslo uzla sa neskrýva: v logu sa zariadenie hlási
                      práve ním, takže bez neho sa riadok s hláškou
                      nespojí.
                    */}
                    <span className="flex flex-col">
                      <span className="text-(--color-text)">{d.name ?? d.label ?? d.nodeId}</span>
                      {(d.name ?? d.label) !== undefined && (
                        <span className="text-xs text-(--color-text-muted)">{d.nodeId}</span>
                      )}
                    </span>
                    <Badge tone={d.online ? 'ok' : 'warn'}>
                      {d.online ? 'Odpovedá' : 'Neodpovedá'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}

            {chyba !== undefined && (
              <Notice tone="danger">
                <p className="break-words">{chyba}</p>
                {/*
                 * Dve uplne rozne priciny s rovnakou hlaskou, obe overene
                 * na hardveri 2026-10-12:
                 *
                 * 1. zariadenie na baterie uz neinzeruje - okno po resete
                 *    je casovo obmedzene a vyprsalo
                 * 2. brana sa pripojila k UZ SPAROVANEMU zariadeniu,
                 *    lebo rucny kod nesie len kratky rozlisovac - to po
                 *    sparovani okno zatvori a spojenie neprijme nikdy,
                 *    takze opakovanie nepomoze
                 *
                 * Rada „skus ten isty kod znova" tu bola do 0.111.1.
                 * Nebola pravdiva: padalo to na periferiovom rozhrani
                 * Bluetooth a opakovanie pomohlo len raz, pri vzniku
                 * fabriky (ADR-0013).
                 */}
                <p className="mt-2">Dve veci, ktoré to spôsobujú najčastejšie:</p>
                <ul className="mt-1 list-inside list-disc">
                  <li>
                    <strong>Zariadenie už neinzeruje.</strong> Zariadenia na batérie sa na Bluetooth
                    hlásia len určitý čas po resete, potom prestanú. Uveď ho znova do párovacieho
                    režimu podľa letáka z krabice a páruj hneď.
                  </li>
                  <li>
                    <strong>Brána sa pripojila k inému zariadeniu.</strong> Ručný kód nesie len
                    krátky rozlišovač (16 možností), takže pri viacerých zariadeniach v dosahu si
                    brána nemá ako vybrať — a keď natrafí na už spárované, spojenie sa dve minúty
                    nenaviaže. Pomôže <strong>obsah QR kódu</strong> (začína <code>MT:</code>),
                    ktorý nesie plný rozlišovač; alebo odpoj ostatné a páruj po jednom.
                  </li>
                </ul>
              </Notice>
            )}
            {hotovo !== undefined && (
              <Notice tone="ok">Spárované: {hotovo}. Nájdeš ho medzi zariadeniami.</Notice>
            )}

            {data.bleNeedsRestart && (
              <Notice tone="warn">
                <p>
                  <strong>Pred ďalším párovaním reštartuj bránu.</strong>
                </p>
                <p className="mt-1">
                  V tomto behu už jedno zariadenie spárované bolo a Bluetooth po tom prestane
                  prijímať inzeráty. Ďalšie zariadenie sa teda nenájde a párovanie po dvoch minútach
                  vyprší — aj keď zariadenie bliká a je v párovacom režime.
                </p>
                <p className="mt-1">
                  Je to chyba v knižnici Bluetooth, nie v nastavení: zložiť ju brána vie, ale znova
                  postaviť nie, lebo knižnica na to nemá verejnú cestu. Jediný spoľahlivý reset je
                  nový proces.
                </p>
                {jeSpravca && <RestartBrany />}
              </Notice>
            )}

            {jeSpravca ? (
              <form className="flex flex-wrap items-end gap-2" onSubmit={sparuj}>
                <div className="min-w-56 flex-1">
                  {/*
                    Pole je rozdelené: kód sa dá napísať, alebo sa dá
                    načítať kamerou. Prečítaný obsah je ten istý `MT:…`,
                    aký by sa písal ručne, takže ďalej ide tou istou
                    cestou a nič sa nepreskakuje.
                  */}
                  <Field
                    label="Párovací kód alebo obsah QR kódu"
                    value={kod}
                    onChange={setKod}
                    required
                    placeholder="1234-567 8901"
                    hint="Pri viacerých zariadeniach v dosahu je obsah QR kódu (MT:…) spoľahlivejší — ručný kód nesie len krátky rozlišovač."
                    akcia={
                      kameraDostupna() ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setSkenuje(true)}
                          aria-label="Naskenovať QR kód kamerou"
                          title="Naskenovať QR kód kamerou"
                        >
                          <IconQr />
                        </Button>
                      ) : undefined
                    }
                  />
                </div>
                <Button type="submit" variant="primary" disabled={paruje}>
                  {paruje ? 'Párujem…' : 'Spárovať'}
                </Button>
              </form>
            ) : (
              <Notice tone="warn">Párovať môže len správca.</Notice>
            )}

            {skenuje && (
              <SkenerQr
                naPrecitanie={(hodnota) => {
                  setKod(hodnota.trim());
                  setSkenuje(false);
                }}
                naZavretie={() => setSkenuje(false)}
              />
            )}

            {paruje && (
              <p className="text-xs text-(--color-text-subtle)">
                Trvá to desiatky sekúnd: zariadenie sa musí nájsť v sieti, vymeniť certifikáty a
                pripojiť do fabriky.
              </p>
            )}
          </>
        )}
      </Card>

      {data.unsupported.length > 0 && (
        <Nepodporovane
          zoznam={data.unsupported}
          jeSpravca={jeSpravca}
          poZmene={nacitanie.refresh}
        />
      )}

      {/*
        Mapa siete Thread je tu, nie v Rádiách: zariadenia, ktoré v tej
        sieti sú, patria k Matteru. Rádiá zostávajú o hardvéri — kanály,
        prepínanie a meranie pásma.
      */}
      {data.enabled && <MapaSieteThread jeSpravca={jeSpravca} />}
    </div>
  );
}

/*
 * Restart brany.
 *
 * Je to obchadzka cudzej chyby, nie vlastnost - a stoji desiatky sekund
 * bez brany, takze svetla, tienenie a zavlaha su medzitym bez nej.
 * Preto to robi clovek kliknutim a s potvrdenim, nie brana sama po
 * kazdom sparovani.
 *
 * Po odoslani sa spojenie pretrhne - to je ocakavane, nie chyba, takze
 * sa to NEHLASI ako chyba. Rozhranie len povie, co sa deje.
 */
function RestartBrany(): ReactNode {
  const [stav, setStav] = useState<'cakam' | 'restartujem' | 'poslane'>('cakam');

  const restartuj = (): void => {
    if (
      !window.confirm(
        'Reštartovať bránu? Desiatky sekúnd nebude fungovať nič — ani svetlá, ani tienenie.',
      )
    ) {
      return;
    }
    setStav('restartujem');
    // Chyba aj uspech znamenaju to iste: brana sa vypina.
    void system.restart().then(
      () => setStav('poslane'),
      () => setStav('poslane'),
    );
  };

  if (stav === 'poslane') {
    return (
      <p className="mt-2 text-sm">
        Brána sa vypína. Docker ju vráti do pár sekúnd — potom obnov stránku a páruj ďalšie
        zariadenie.
      </p>
    );
  }

  return (
    <div className="mt-2">
      <Button onClick={restartuj} disabled={stav === 'restartujem'}>
        {stav === 'restartujem' ? 'Reštartujem…' : 'Reštartovať bránu'}
      </Button>
    </div>
  );
}

/*
 * Sparovane zariadenia, ktorym brana nerozumie.
 *
 * Preco to tu vobec je: zariadenie sa sparuje do nasej fabriky JESTE
 * predtym, nez brana zisti, ci v nom pozna nejaku schopnost. Ked
 * nepozna, do modelu sa nezaradi - a do 0.112.0 nebolo nikde vidno
 * a nedalo sa odobrat, lebo mazanie zariadeni hlada zariadenie
 * v registri modelu. Zostalo osirene (dva ovladace IKEA, 2026-10-13).
 *
 * Zoznam clusterov je tu zamerne: podla neho sa mapovanie da doplnit,
 * namiesto hadania, co to zariadenie je.
 */
function Nepodporovane({
  zoznam,
  jeSpravca,
  poZmene,
}: {
  readonly zoznam: readonly NepodporovaneMatter[];
  readonly jeSpravca: boolean;
  readonly poZmene: () => void;
}): ReactNode {
  const [odoberany, setOdoberany] = useState<string | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const odober = (uzol: NepodporovaneMatter): void => {
    const meno = uzol.label ?? uzol.nodeId;
    if (!window.confirm(`Odobrať ${meno} z fabriky Matter? Spárovať sa bude musieť znova.`)) {
      return;
    }
    setChyba(undefined);
    setOdoberany(uzol.nodeId);
    void matter
      .odober(uzol.nodeId)
      .then(() => poZmene())
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zariadenie sa nepodarilo odobrať.');
      })
      .finally(() => setOdoberany(undefined));
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <div>
        <h2 className="text-base font-medium text-(--color-text)">Spárované, ale nepodporované</h2>
        <p className="mt-1 text-sm text-(--color-text-muted)">
          Tieto zariadenia sú v našej fabrike Matter, ale brána v nich nepozná ani jednu schopnosť,
          takže ich nemá čo poslať do Loxone. <strong>Netreba ich resetovať</strong> — keď sa doplní
          podpora, nabehnú samé. Odobrať ich treba len vtedy, keď ich chceš spárovať inde.
        </p>
      </div>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      <ul className="flex flex-col gap-3">
        {zoznam.map((uzol) => (
          <li
            key={uzol.nodeId}
            className="flex flex-col gap-1 border-t border-(--color-border) pt-3 first:border-0 first:pt-0"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-(--color-text)">{uzol.label ?? uzol.nodeId}</span>
              {jeSpravca && (
                <Button
                  variant="ghost"
                  onClick={() => odober(uzol)}
                  disabled={odoberany !== undefined}
                >
                  {odoberany === uzol.nodeId ? 'Odoberám…' : 'Odobrať'}
                </Button>
              )}
            </div>
            {(uzol.vendor !== undefined || uzol.product !== undefined) && (
              <p className="text-xs text-(--color-text-muted)">
                {[uzol.vendor, uzol.product].filter((x) => x !== undefined).join(' · ')}
              </p>
            )}
            {uzol.clusters.length > 0 && (
              <p className="text-xs text-(--color-text-subtle) break-words">
                Hlási: {uzol.clusters.join(', ')}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/*
 * Sonda pripravenosti na Matter.
 *
 * Patri sem, k Matteru - nie medzi nastavenia brany. Otazku „oplati sa
 * to vobec stavat?" uz zodpovedala; uzitocna zostava v den, ked sa siet
 * zmeni (napriklad prechod z Wi-Fi na kabel) alebo ked Matter zacne
 * zlyhavat a treba vediet, ci multicast vobec chodi.
 */
const POPIS_VERDIKTU: Record<
  string,
  { readonly text: string; readonly tone: 'ok' | 'warn' | 'danger' }
> = {
  pripravene: { text: 'Sieť je pripravená', tone: 'ok' },
  's-vyhradami': { text: 'Pôjde to, ale s výhradami', tone: 'warn' },
  neprejde: { text: 'V tejto sieti Matter nepobeží', tone: 'danger' },
};

function SondaMatter(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [bezi, setBezi] = useState(false);
  const [vysledok, setVysledok] = useState<PripravenostMatter | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const zmeraj = (): void => {
    setChyba(undefined);
    setBezi(true);
    void diagnostics
      .matter()
      .then(({ readiness }) => setVysledok(readiness))
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Sondu sa nepodarilo spustiť.');
      })
      .finally(() => setBezi(false));
  };

  const verdikt = vysledok === undefined ? undefined : POPIS_VERDIKTU[vysledok.verdict];

  return (
    <div className="flex flex-col gap-3 rounded-(--radius-control) border border-(--color-border) p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-(--color-text)">Pripravenosť siete</span>
        {jeSpravca && (
          <Button variant="ghost" onClick={zmeraj} disabled={bezi}>
            {bezi ? 'Meriam…' : vysledok === undefined ? 'Skontrolovať' : 'Zmerať znova'}
          </Button>
        )}
      </div>

      <p className="text-xs text-(--color-text-subtle)">
        Matter stojí na IPv6 multicaste a mDNS. Sonda overí, či to v tejto sieti funguje a či brána
        vidí zariadenia na druhej vrstve — cez hranicu VLAN Matter nefunguje.
      </p>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      {vysledok !== undefined && verdikt !== undefined && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={verdikt.tone}>{verdikt.text}</Badge>
            <span className="text-xs text-(--color-text-subtle)">
              merané {new Date(vysledok.at).toLocaleString('sk-SK')}
            </span>
          </div>

          {vysledok.problems.length > 0 && (
            <Notice tone={vysledok.verdict === 'neprejde' ? 'danger' : 'warn'}>
              <ul className="flex list-disc flex-col gap-1 pl-4">
                {vysledok.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Notice>
          )}

          <dl className="text-xs">
            <DataRow label="IPv6">{vysledok.ipv6 ? 'dostupné' : 'chýba'}</DataRow>
            <DataRow label="Odpovede mDNS">
              {vysledok.mdnsRespondersV4} cez IPv4 · {vysledok.mdnsRespondersV6} cez IPv6
            </DataRow>
            <DataRow label="Zariadenia Matter v dosahu">
              {vysledok.matterServices.length === 0 ? 'žiadne' : vysledok.matterServices.join(', ')}
            </DataRow>
            <DataRow label="Mimo podsiete brány">
              {vysledok.devicesOutsideSubnet.length === 0
                ? 'žiadne'
                : `${vysledok.devicesOutsideSubnet.length} (${vysledok.devicesOutsideSubnet.slice(0, 3).join(', ')})`}
            </DataRow>
          </dl>
        </div>
      )}
    </div>
  );
}
